/**
 * 表プレビューのヘッダー。条件分岐の最大の深さ D に対して D+1 段になる(分岐が無ければ 1 段)。
 * 深さ k の条件分岐は配下の列をまとめた帯を k 段目に出し、列見出しは自分の段から最下段まで rowSpan で伸ばす。
 * 段の並べ方は getTableHeaderRows が決め、ここは並べるだけ。
 */
import { colors } from '@kmns-internal/furikake'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import {
  BAND_CONTENT_HEIGHT,
  CONDITION_BAND_HEIGHT,
  getLogicNumberByQuestionId,
  getTableHeaderRows,
  headerStickyTop,
  type TableDisplayColumn,
} from '../model'
import { LogicNumberChip } from './LogicNumberChip'
import type { TableEditHandlers } from './TablePreview'
import { TablePreviewBranchHeader } from './TablePreviewBranchHeader'
import { TablePreviewColumnHeader } from './TablePreviewColumnHeader'
import { TablePreviewRowHeadingHeader } from './TablePreviewRowHeadingHeader'

type BaseProps = {
  displayColumns: TableDisplayColumn[]
  templateRow: TemplateNodeSchema
  rowHeadingHidden: boolean
  activeNodeId: number
}

type InteractiveProps = BaseProps & {
  interactive: true
  handlers: TableEditHandlers
}

type ReadonlyProps = BaseProps & {
  interactive?: false
}

type Props = InteractiveProps | ReadonlyProps

export const TablePreviewHeader = (props: Props) => {
  const { displayColumns, templateRow, rowHeadingHidden, activeNodeId } = props
  const rows = getTableHeaderRows(displayColumns)
  const logicNumberByQuestionId = getLogicNumberByQuestionId(displayColumns)
  const modeProps = props.interactive
    ? { interactive: true as const, handlers: props.handlers }
    : { interactive: false as const }

  return (
    <Box component="thead">
      {rows.map((cells, depth) => (
        <Box component="tr" key={depth}>
          {/* 行操作メニュー列は編集時のみ表示する。全段にまたがる。 */}
          {depth === 0 && props.interactive && (
            <Box
              component="th"
              rowSpan={rows.length}
              sx={theme => ({
                position: 'sticky',
                top: 0,
                zIndex: 3,
                width: theme.spacing(5),
                minWidth: theme.spacing(5),
                p: 0,
                borderRight: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: colors.white,
                verticalAlign: 'middle',
              })}
            />
          )}
          {depth === 0 && (
            <TablePreviewRowHeadingHeader
              templateRow={templateRow}
              hidden={rowHeadingHidden}
              rowSpan={rows.length}
              selected={!rowHeadingHidden && activeNodeId === templateRow.id}
              {...modeProps}
            />
          )}
          {cells.map(cell => {
            if (cell.kind === 'band') {
              const logicNumber = logicNumberByQuestionId.get(
                cell.parentQuestionId,
              )
              return (
                <Box
                  key={`band-${cell.logicId}`}
                  component="th"
                  colSpan={cell.colSpan}
                  sx={theme => ({
                    position: 'sticky',
                    top: headerStickyTop(theme, depth),
                    zIndex: 3,
                    height: CONDITION_BAND_HEIGHT(theme),
                    px: 1,
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: colors.primary.selected,
                    textAlign: 'left',
                    verticalAlign: 'middle',
                  })}
                >
                  {/*
                    帯は配下の列ぶんだけ横に長いので、左端に置いた文言は横スクロールで
                    画面外へ流れていく。sticky で左に貼り付けて、帯が見えている間は
                    文言も見えるようにする。止まる位置は帯の右端。
                  */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      position: 'sticky',
                      left: 0,
                      width: 'fit-content',
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    {logicNumber && (
                      // 帯と同じ背景色になり箱が浮いて見えるため、帯の中では背景を敷かない。
                      <LogicNumberChip
                        logicNumber={logicNumber}
                        disableBackground
                      />
                    )}
                    {/* 縮むのは文言だけ。flex アイテムは既定で内容幅より縮まないため minWidth: 0 で省略を効かせる。 */}
                    <Typography
                      variant="caption"
                      color="primary"
                      noWrap
                      sx={{ minWidth: 0 }}
                    >
                      {cell.label}
                    </Typography>
                    {props.interactive && (
                      <Tooltip title="条件分岐を削除">
                        <IconButton
                          aria-label="条件分岐を削除"
                          onClick={() => props.handlers.onDeleteLogic(cell)}
                          sx={theme => ({
                            position: 'relative',
                            flexShrink: 0,
                            p: 0.25,
                            width: BAND_CONTENT_HEIGHT(theme),
                            height: BAND_CONTENT_HEIGHT(theme),
                            // 見た目は帯に収まる大きさにするが、対応 UA に iPad Chrome が含まれるため
                            // 当たり判定は 24px 確保する(WCAG 2.2 の Target Size(Minimum))。
                            // 疑似要素はレイアウトに影響しないので、帯の高さは変わらない。
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              inset: theme.spacing(-0.25),
                            },
                          })}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: 16, color: colors.error.main }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              )
            }

            const { column, rowSpan } = cell
            return column.kind === 'column' ? (
              <TablePreviewColumnHeader
                key={column.key}
                column={column.question}
                columnIndex={column.columnIndex}
                rowSpan={rowSpan}
                selected={activeNodeId === column.columnId}
                logicNumber={logicNumberByQuestionId.get(column.columnId)}
                {...modeProps}
              />
            ) : (
              <TablePreviewBranchHeader
                key={column.key}
                branch={column}
                rowSpan={rowSpan}
                selected={
                  column.question != null && activeNodeId === column.question.id
                }
                logicNumber={
                  column.question &&
                  logicNumberByQuestionId.get(column.question.id)
                }
                {...modeProps}
              />
            )
          })}
        </Box>
      ))}
    </Box>
  )
}
